import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown, ShieldCheck } from "lucide-react";

const HERO_IMG = "https://images.pexels.com/photos/10394772/pexels-photo-10394772.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 700], [0, 90]);
  const yText = useTransform(scrollY, [0, 700], [0, -40]);

  return (
    <section id="top" className="relative min-h-screen flex items-center overflow-hidden grain bg-[#fafafa] pt-36 pb-20">
      <div
        className="absolute -top-40 -right-40 w-[34rem] h-[34rem] rounded-full bg-emerald-100/60 blur-3xl"
        aria-hidden="true"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 w-full grid lg:grid-cols-12 gap-12 lg:gap-8 items-center relative">
        <motion.div style={{ y: yText }} className="lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-emerald-600/30 bg-emerald-50 px-4 py-1.5 mb-8"
            data-testid="hero-badge"
          >
            <ShieldCheck className="w-4 h-4 text-emerald-700" />
            <span className="text-xs font-mono font-semibold uppercase tracking-[0.22em] text-emerald-700">
              In collaborazione con DoYou Italy
            </span>
          </motion.div>

          <h1
            className="font-display font-extrabold uppercase tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-7xl text-slate-900"
            data-testid="hero-title"
          >
            <span className="line-mask">
              <motion.span custom={0} variants={lineVariant} initial="hidden" animate="show" className="block">
                La tua mobilità.
              </motion.span>
            </span>
            <span className="line-mask">
              <motion.span custom={1} variants={lineVariant} initial="hidden" animate="show" className="block">
                Semplice.
              </motion.span>
            </span>
            <span className="line-mask">
              <motion.span custom={2} variants={lineVariant} initial="hidden" animate="show" className="block text-emerald-600">
                Sicura.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            Auto, furgoni e veicoli commerciali a noleggio in tutta Italia.
            Trasparenza totale, zero sorprese al ritiro e un operatore dedicato, sempre.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#preventivo"
              data-testid="hero-quote-button"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-8 py-4 text-sm font-semibold text-white hover:bg-emerald-700 hover:text-white transition-colors duration-300"
            >
              Richiedi preventivo rapido
              <ArrowDown className="w-4 h-4" />
            </a>
            <a
              href="tel:+393773899146"
              data-testid="hero-phone-cta"
              className="inline-flex items-center justify-center gap-3 rounded-full border-2 border-emerald-600 bg-white px-8 py-4 text-sm font-bold text-emerald-700 hover:bg-emerald-600 hover:text-white transition-colors duration-300"
            >
              <Phone className="w-4 h-4" />
              Chiama ora: +39 377 389 9146
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-12 flex flex-wrap gap-x-10 gap-y-4"
          >
            {[
              ["H24", "Assistenza telefonica"],
              ["0 €", "Costi nascosti"],
              ["100%", "Flotta revisionata"],
            ].map(([num, label]) => (
              <div key={label}>
                <p className="font-display text-2xl font-extrabold text-slate-900">{num}</p>
                <p className="text-xs font-mono uppercase tracking-[0.18em] text-slate-500 mt-1">{label}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div style={{ y: yImg }} className="rounded-[2rem] overflow-hidden border border-slate-200 shadow-2xl shadow-emerald-900/10">
              <img
                src={HERO_IMG}
                alt="Auto sportiva e di lusso Noleggio Sicuro"
                className="w-full h-[420px] lg:h-[520px] object-cover scale-105"
                data-testid="hero-image"
              />
            </motion.div>
            <div className="absolute -bottom-6 -left-4 sm:-left-8 bg-slate-900 text-white rounded-2xl px-6 py-5 shadow-xl">
              <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-400">Pronto al ritiro</p>
              <p className="font-display font-bold text-lg mt-1">Consegna in tutta Italia</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
